<!--
  종사자 의견 및 조치계획 입력 (818~821 신규작성 공통)
  v-model 에 form.subInfo 를 통째로 넘긴다. 의견목록·특이사항없음과, 목록·검색용으로 합친 종사자의견·조치계획·조치결과 글을 함께 돌려준다.
  발언자 한 명 아래 의견이 여러 줄 달리고, 의견마다 조치계획이 1:1 로 붙는다 (조치계획은 비워도 된다).
-->
<template>
  <div class="opinion-editor">
    <!-- 인쇄 -->
    <div v-if="isPrint">
      <p v-if="특이사항없음" class="print-textarea">특이사항 없음</p>
      <p v-else-if="!printRows.length" class="print-textarea">&nbsp;</p>
      <table v-else class="oe-print">
        <colgroup>
          <col width="110" />
          <col width="36" />
          <col />
          <col />
          <col />
          <col width="70" />
        </colgroup>
        <thead>
          <tr>
            <th>발언자</th>
            <th>번호</th>
            <th>의견</th>
            <th>조치계획</th>
            <th>조치결과</th>
            <th>개선결과</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in printRows" :key="`p-${i}`">
            <td v-if="row.first" :rowspan="row.span">{{ row.발언자 }}</td>
            <td class="text-center">{{ i + 1 }}</td>
            <td class="pre">{{ row.의견 }}</td>
            <td class="pre">{{ row.조치계획 }}</td>
            <td class="pre">{{ row.조치결과 }}</td>
            <td class="text-center">{{ row.개선결과 }}{{ row.개선결과 === "기타" && row.기타내용 ? ` (${row.기타내용})` : "" }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 입력 -->
    <div v-else>
      <div v-if="특이사항없음" class="oe-none">특이사항 없음 · 계획 및 개선 조치를 진행할 만한 의견이 없는 회의입니다.</div>
      <template v-else>
        <div class="oe-head">
          <span>발언자</span>
          <span>번호</span>
          <span>의견</span>
          <span>조치계획</span>
          <span></span>
        </div>
        <div v-for="(block, bi) in blocks" :key="block.key" class="oe-block">
          <div class="oe-speaker">
            <ml-input class="w-full" v-model="block.발언자" placeholder="발언자" :readonly="readonly" />
            <small>의견 {{ block.rows.length }}건</small>
          </div>
          <div class="oe-lines">
            <div v-for="(row, ri) in block.rows" :key="row.key" class="oe-line">
              <span class="oe-no">{{ numberOf(bi, ri) }}</span>
              <ml-textarea :rows="rowsFor(row.의견)" class="w-full mb-0" v-model="row.의견" placeholder="의견 한 건" :readonly="readonly" />
              <ml-textarea :rows="rowsFor(row.조치계획)" class="w-full mb-0" v-model="row.조치계획" placeholder="조치계획" :readonly="readonly" />
              <button v-if="!readonly" type="button" class="oe-del" title="이 의견 삭제" @click="removeRow(bi, ri)">×</button>
            </div>
            <button v-if="!readonly" type="button" class="oe-link" @click="addRow(bi)">+ 이 발언자 의견 추가</button>
          </div>
        </div>
      </template>

      <div class="oe-foot">
        <vs-button v-if="!readonly && !특이사항없음" color="primary" type="border" @click="addBlock">+ 발언자 추가</vs-button>
        <vs-checkbox v-model="특이사항없음" :disabled="readonly" class="ml-0">특이사항 없음</vs-checkbox>
        <span class="ml-auto text-sm oe-count">
          발언자 <b>{{ 특이사항없음 ? 0 : blocks.length }}</b>명, 의견 <b>{{ 특이사항없음 ? 0 : rowCount }}</b>건
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { newOpinion, normalizeOpinions, applyOpinions, fillGroupIds } from "./opinion.js";

let seq = 0;
const nextKey = () => ++seq;

export default {
  name: "OpinionEditor",
  props: {
    value: { type: Object, default: () => ({}) },
    isPrint: Boolean,
    readonly: Boolean,
  },
  data() {
    return {
      // blocks = [{ key, 발언자, rows: [{ key, 의견, 조치계획, rest }] }]  rest 에는 조치결과·개선결과·기타내용·묶음이 남아 있다.
      blocks: [],
      특이사항없음: false,
      lastSignature: "",
    };
  },
  computed: {
    rowCount() {
      return this.blocks.reduce((n, b) => n + b.rows.length, 0);
    },
    // blocks → 의견목록. 의견·조치계획이 모두 빈 줄은 저장하지 않는다 (발언자만 적힌 빈 줄 포함).
    list() {
      const list = [];
      for (const block of this.blocks) {
        for (const row of block.rows) {
          if (!row.의견 && !row.조치계획) continue;
          list.push(newOpinion({ ...row.rest, 발언자: block.발언자, 의견: row.의견, 조치계획: row.조치계획 }));
        }
      }
      return fillGroupIds(list); // 새 줄은 기존 묶음과 겹치지 않는 번호
    },
    // 인쇄용: 연속된 같은 발언자는 칸을 합친다
    printRows() {
      const list = this.list;
      return list.map((o, i) => {
        const first = i === 0 || list[i - 1].발언자 !== o.발언자;
        let span = 1;
        if (first) while (list[i + span] && list[i + span].발언자 === o.발언자) span++;
        return { ...o, first, span };
      });
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        this.load(value);
      },
    },
    blocks: {
      deep: true,
      handler() {
        this.emit();
      },
    },
    특이사항없음() {
      this.emit();
    },
  },
  methods: {
    signature(특이사항없음, 의견목록) {
      return JSON.stringify([!!특이사항없음, 의견목록]);
    },
    load(subInfo) {
      const n = normalizeOpinions(subInfo);
      const signature = this.signature(n.특이사항없음, n.의견목록);
      if (signature === this.lastSignature) return; // 내가 emit 한 값이 되돌아온 것
      this.lastSignature = signature;
      this.특이사항없음 = n.특이사항없음;
      this.blocks = this.toBlocks(n.의견목록);
      if (!this.blocks.length && !this.특이사항없음) this.blocks.push(this.newBlock());
    },
    emit() {
      const 의견목록 = this.특이사항없음 ? [] : this.list;
      const signature = this.signature(this.특이사항없음, 의견목록);
      if (signature === this.lastSignature) return;
      this.lastSignature = signature;
      this.$emit("input", applyOpinions(this.value, 의견목록, this.특이사항없음));
    },
    toBlocks(list) {
      const blocks = [];
      for (const o of list) {
        const { 발언자, 의견, 조치계획, ...rest } = o;
        const last = blocks[blocks.length - 1];
        const row = { key: nextKey(), 의견: 의견 || "", 조치계획: 조치계획 || "", rest };
        if (last && last.발언자 === (발언자 || "")) last.rows.push(row);
        else blocks.push({ key: nextKey(), 발언자: 발언자 || "", rows: [row] });
      }
      return blocks;
    },
    newRow() {
      return { key: nextKey(), 의견: "", 조치계획: "", rest: {} };
    },
    newBlock() {
      return { key: nextKey(), 발언자: "", rows: [this.newRow()] };
    },
    // 글 줄 수에 맞춰 2~8줄. 옛 문서의 긴 글이 한 줄에 눌려 보이지 않도록
    rowsFor(text) {
      const lines = `${text || ""}`.split("\n").length;
      return Math.min(8, Math.max(2, lines));
    },
    numberOf(bi, ri) {
      let n = ri + 1;
      for (let i = 0; i < bi; i++) n += this.blocks[i].rows.length;
      return n;
    },
    addBlock() {
      this.blocks.push(this.newBlock());
    },
    addRow(bi) {
      this.blocks[bi].rows.push(this.newRow());
    },
    removeRow(bi, ri) {
      this.blocks[bi].rows.splice(ri, 1);
      if (!this.blocks[bi].rows.length) this.blocks.splice(bi, 1);
      if (!this.blocks.length) this.blocks.push(this.newBlock());
    },
  },
};
</script>

<style lang="scss" scoped>
.oe-head,
.oe-line {
  display: grid;
  gap: 8px;
  align-items: center; // 시안처럼 번호·삭제 × 를 글상자 세로 가운데에
}
// 시안의 머리줄: 연회색 배경, 가운데 정렬, 500 굵기
.oe-head {
  grid-template-columns: 150px 44px 1fr 1fr 36px;
  padding: 9px 0;
  background: #f8f8f8;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 13px;
  font-weight: 500;
  color: #3c3c3c;
  text-align: center;
}
.oe-line {
  grid-template-columns: 44px 1fr 1fr 36px;
  & + .oe-line {
    margin-top: 8px;
  }
}
.oe-block {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px dashed #e5e5e5;
}
.oe-speaker small {
  display: block;
  margin-top: 4px;
  color: #999;
  font-size: 12px;
}
.oe-no {
  text-align: center;
  color: #888;
}
// 시안처럼 테두리 없는 × 글자만
.oe-del {
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  color: #aaa;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  &:hover {
    color: #ea5455;
  }
}
.oe-link {
  margin-top: 6px;
  padding: 2px 0;
  border: none;
  background: none;
  color: rgba(var(--vs-primary), 1);
  font-size: 13px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.oe-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-top: 10px;
  color: #666;
}
.oe-count {
  color: #777;
  b {
    color: #3c3c3c;
    font-weight: 700;
  }
}
.oe-none {
  padding: 14px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #fafafa;
  color: #666;
}
.oe-print {
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 6px 8px;
    font-size: 12px;
    vertical-align: top;
    text-align: left;
  }
  th {
    background: #fafafa;
    font-weight: 500;
    text-align: center;
  }
  .pre {
    white-space: pre-wrap;
    word-break: break-all;
  }
}
@media (max-width: 767px) {
  .oe-head {
    display: none;
  }
  .oe-block {
    grid-template-columns: 1fr;
  }
  .oe-line {
    grid-template-columns: 44px 1fr 36px;
    & > :nth-child(3) {
      grid-column: 2;
    }
    & > .oe-del {
      grid-column: 3;
      grid-row: 1;
    }
  }
}
</style>
